/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { Theme, ThemeOptions } from '@mui/material';
import type { GetTheme } from '../../theme';

import { deepmerge } from '@mui/utils';
import { createTheme } from '@mui/material';

import { shadows } from './token/shadows';
import { colors } from './token/colors';
import { letterSpacing } from './token/sizes';
import {
  fontFamilyRobotoString,
  typography,
  fontWeights,
} from './token/typography';
import { robotoBold, robotoRegular } from '../../fonts/roboto';

const smavaThemeOptions: ThemeOptions = {
  palette: colors,
  typography: {
    fontFamily: fontFamilyRobotoString,
  },
  shape: {
    borderRadius: 2,
  },
};

const withSmavaThemeComposition = (theme: Theme): ThemeOptions => ({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '@font-face': [robotoRegular, robotoBold],
          html: {
            touchAction: 'manipulation',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          width: '100%', // This is needed for IE11. Otherwise, it wouldn't wrap the text correctly
        },
        contained: {
          marginLeft: 0,
          paddingLeft: theme.spacing(1),
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          letterSpacing,
        },
        ...typography,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&$disabled': {
            backgroundColor: theme.palette.grey['50'],
            color: theme.palette.grey['600'],
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: `0 0 2px ${theme.palette.primary.main}`,
            borderWidth: '1px',
          },
          '&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
            boxShadow: `0 0 2px ${theme.palette.error.main}`,
            borderWidth: '1px',
          },
        },
        inputAdornedEnd: {
          paddingRight: 0,
          '& > p': {
            marginBottom: 0,
          },
        },
        adornedEnd: {
          paddingRight: 0,
        },
        input: {
          color: theme.palette.grey['900'],
          padding: '12.5px 8px',
          border: 0,
          '&:-webkit-autofill,&:-webkit-autofill:hover,&:-webkit-autofill:focus':
            {
              WebkitBoxShadow: shadows.inputOutlined,
              WebkitTextFillColor: theme.palette.text.secondary,
            },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionEnd: {
          position: 'absolute',
          right: '8px',
          '& > p': {
            marginBottom: '0 !important',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&> div': {
            padding: 0,
          },
          '&.Mui-expanded': {
            margin: 0,
            '::before': {
              opacity: 1,
              display: 'block !important',
            },
            '&:first-of-type': {
              '::before': {
                opacity: 1,
                display: 'none !important',
              },
            },
          },
          '& .MuiTypography-root': {
            margin: '0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            minHeight: 0,
          },
        },
        content: {
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&.MuiInputAdornment-positionEnd': {
            position: 'relative',
          },
        },
        inputRoot: {
          padding: '5px 0 5px 0 !important',
        },
        input: {
          // tried many ways of not having to add this important, but somehow failed..
          paddingLeft: '8px !important',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            ':hover': {
              // @ts-ignore
              background: theme.palette.primary['50'],
            },
          },
        },
        {
          props: { variant: 'contained' },
          style: {
            ':hover': {
              // @ts-ignore
              background: theme.palette.primary['700'],
            },
          },
        },
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            fontSize: '16px',
            padding: '17px 50px',
            fontWeight: fontWeights.medium,
            lineHeight: '22px',
          },
        },
        {
          props: { variant: 'contained', size: 'medium' },
          style: {
            fontSize: '14px',
            padding: '13px 50px',
            fontWeight: fontWeights.medium,
            lineHeight: '22px',
          },
        },
        {
          props: { variant: 'contained', size: 'small' },
          style: {
            fontSize: '14px',
            padding: '9px 50px',
            fontWeight: fontWeights.medium,
            lineHeight: '22px',
          },
        },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            fontSize: '16px',
            padding: '17px 50px',
            fontWeight: fontWeights.medium,
            lineHeight: '22px',
          },
        },
        {
          props: { variant: 'contained', color: 'secondary', size: 'medium' },
          style: {
            fontSize: '14px',
            padding: '13px 50px',
            fontWeight: fontWeights.medium,
            lineHeight: '22px',
          },
        },
        {
          props: { variant: 'contained', color: 'secondary', size: 'small' },
          style: {
            fontSize: '14px',
            padding: '9px 50px',
            fontWeight: fontWeights.medium,
            lineHeight: '22px',
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: '2px',
          textTransform: 'initial',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '100%',
          display: 'block',
          marginBottom: theme.spacing(2),
          marginTop: theme.spacing(1),
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: theme.palette.grey[900],

          '&.Mui-focused': {
            color: theme.palette.grey[900],
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          color: '#323232',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          colour: '#323232',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '10px 14px',
          fontSize: '16px',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          // @ts-ignore
          color: theme.palette.primary['500'],
        },
        tooltip: {
          background: theme.palette.background.default,
          // @ts-ignore
          color: theme.palette.secondary['900'],
          // @ts-ignore
          border: `1px solid ${theme.palette.primary['500']}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 2,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // removes arrows from inputs of type number
        input: {
          '&[type=number]': {
            '-moz-appearance': 'textfield',
          },
          '&::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          },
          // @ts-ignore
          border: `1px solid ${theme.palette.secondary['300']}`,
        },
      },
    },
  },
});

export const getTheme: GetTheme = ({
  options,
  optionsWithThemeValues = () => ({}),
}) => {
  let smavaBaseTheme = createTheme(deepmerge(smavaThemeOptions, options));
  smavaBaseTheme = createTheme(
    smavaBaseTheme,
    withSmavaThemeComposition(smavaBaseTheme),
  );
  smavaBaseTheme = createTheme(
    smavaBaseTheme,
    optionsWithThemeValues(smavaBaseTheme),
  );
  return smavaBaseTheme;
};
